import _ from 'lodash'
import { createSelector } from 'reselect'
import { getRiskLevel, groupDependentFields } from '../common/utils'
import { isDependentFieldHidden } from '../common/validation/functions'
import { formatWithResidentInfo } from './helpers/InitialAssessments/formatFieldOutput'
import {
  getFormsList,
  getResidentsList,
  getRiskAssessmentList,
  getUserHome,
  getUsersList
} from './Lists'

interface RiskAssessment {
  id: string
  form: Form
  createdBy?: string
  editedBy?: string
  riskLevel?: string
  fields: Record<string, unknown>
}

interface InitialAssessment {
  id: string
  form: Form
}
interface Resident {
  id: string
}

interface FormField {
  key: string
  label: string
  description?: string
  optionType?: string
  suggestedActions?: unknown
  values: Array<{
    value: unknown
    label: string
    description?: string
  }>
  dependsOnKey?: string
  dependsOnValue?: unknown
}

interface Form {
  id: string
  name: string
  formFields: Array<FormField>
  ranges?: Array<{ low: number; high: number; risk: string }>
  fields?: Record<string, unknown>
  formData?: unknown
}

interface RootState {
  data: {
    'risk-assessments': Array<RiskAssessment>
    'initial-assessments': Array<InitialAssessment>
    residents: Array<Resident>
    forms: Array<Form>
  }
  forms: {
    createRiskAssessment?: RiskAssessment
  }
  carePlans: {
    riskAssessmentSortType: unknown
  }
}

interface SelectorProps {
  riskAssessment?: RiskAssessment
  risk_assessment?: string
  resident?: string
  form_id?: string
  router?: {
    params: {
      id?: string
      risk_assessment_history_id?: string
      risk_assessment_id?: string
      pre_assessment_id?: string
    }
  }
}

const getAssessmentById = (
  state: RootState,
  { risk_assessment }: SelectorProps
) => _.find(state.data['risk-assessments'], { id: risk_assessment })

const getResidentById = (state: RootState, { resident }: SelectorProps) =>
  _.find(state.data.residents, { id: resident })

const getRiskAssessmentForm = (state: RootState, props: SelectorProps) => {
  if (props?.riskAssessment) {
    return props.riskAssessment
  }
  return state.forms.createRiskAssessment
}

const getRiskAssessmentSortType = (state: RootState) =>
  state.carePlans.riskAssessmentSortType

// Gets a user-generated form from the state based on the current router params.
const resolveForm = (state: RootState, props: SelectorProps): Form | null => {
  const params = props.router?.params
  const riskAssessmentId =
    params?.risk_assessment_history_id || params?.risk_assessment_id

  if (riskAssessmentId) {
    const ra = _.find(state.data['risk-assessments'], { id: riskAssessmentId })
    if (ra) return ra.form
    // not in Redux yet — fall through to form_id
  }

  if (params?.pre_assessment_id) {
    return (
      _.find(state.data['initial-assessments'], {
        id: params.pre_assessment_id
      })?.form ?? null
    )
  }

  if (props.form_id) {
    return (
      (_.find(state.data.forms, { id: props.form_id }) as Form | undefined) ??
      null
    )
  }

  return null
}

export const getFormByParams = (state: RootState, props: SelectorProps) => {
  const form = resolveForm(state, props)
  const residentId = props.router?.params.id
  if (!form?.formFields || !residentId) {
    return form
  }

  const resident = _.find(getResidentsList(state), { id: residentId })
  if (!resident) {
    return form
  }

  const home = getUserHome(state)
  return formatLabelsWithResidentInfo(
    form,
    resident,
    home?.preferredPeopleNominative
  ) as Form
}

export const getRiskAssessments = createSelector(
  [getUsersList, getRiskAssessmentList],
  (carers, riskAssessments) => {
    if (!carers || !riskAssessments) {
      return null
    }

    const carersById = _.keyBy(carers, 'id')
    return riskAssessments.map((row: RiskAssessment) => ({
      ...row,
      createdBy: row.createdBy && carersById[row.createdBy],
      editedBy: row.editedBy && carersById[row.editedBy]
    }))
  }
)

// Calculates the risk score and level for the current form
export const calculateAssessmentRisk = createSelector(
  [getFormByParams, getRiskAssessmentForm],
  (form, riskForm) => {
    if (!form || !riskForm) {
      return null
    }
    let level = 'incomplete'
    let score = 0

    const riskFields = form.formFields.filter(
      (field) => field.optionType === 'riskScore'
    )

    // only show risk result when every non-hidden risk field has been filled in
    if (isAllRiskFieldsFilled(riskFields, riskForm.fields)) {
      score = getRiskScore(riskFields, riskForm)
      level = getRiskLevel(form.ranges ?? [], score)
    }

    return { level, score }
  }
)

export const concatenateFormsAndAssessments = createSelector(
  [getFormsList, getRiskAssessmentList],
  (forms, riskAssessments) => {
    if (!forms || !riskAssessments) {
      return null
    }
    const formsWithoutAssessments = _.filter(forms, (form) => {
      if (form.isRevision) {
        return false
      }
      const assessmentForForm = _.find(riskAssessments, (assessment) => {
        if (assessment.form?.name) {
          return form.formId === assessment.form.formId
        }
      })
      return !assessmentForForm
    })

    const allItems = [...formsWithoutAssessments, ...riskAssessments]

    return _.sortBy(allItems, (item) => {
      return item.form?.name ? item.form.name : item.name
    })
  }
)

export const formatFieldsForAssessment = createSelector(
  [getFormByParams],
  (form) => {
    if (!form) {
      return null
    }

    const withKeys = _.map(form.formFields, formatFieldWithKeys) as any[]
    const formFields = groupDependentFields(withKeys)

    return { ...form, formFields }
  }
)

export const formatFieldsForAssessmentDisplay = createSelector(
  [getFormByParams],
  (form) => {
    if (!form) {
      return null
    }

    const withKeys = _.map(form.formFields, formatFieldWithKeys) as any[]
    const formFields = groupDependentFields(withKeys).map((field) => {
      if (field.dependentFields) {
        return {
          ...field,
          dependentFields: field.dependentFields.filter(
            (entry: any) => entry.type !== 'suggestedActions'
          )
        }
      }
      return { ...field }
    })

    return { ...form, formFields }
  }
)

export const formatRiskAssessment = createSelector(
  [getAssessmentById, getResidentById, getUserHome],
  (riskAssessment, resident, home) => {
    if (!riskAssessment || !resident) {
      return null
    }
    return riskAssessment.form
      ? {
          ...riskAssessment,
          form: formatLabelsWithResidentInfo(
            riskAssessment.form,
            resident,
            home?.preferredPeopleNominative
          )
        }
      : riskAssessment
  }
)

export const formatRiskAssessmentPDF = createSelector(
  [getAssessmentById, getResidentById, getUserHome],
  (riskAssessment, resident, home) => {
    if (!riskAssessment || !resident) {
      return null
    }

    let formattedForm

    if (riskAssessment.form) {
      formattedForm = formatLabelsWithResidentInfo(
        riskAssessment.form,
        resident,
        home?.preferredPeopleNominative
      ) as Form
    }

    return riskAssessment.form
      ? {
          ...riskAssessment,
          form: {
            ...formattedForm,
            // biome-ignore lint/style/noNonNullAssertion: <explanation>
            formFields: formattedForm!.formFields.filter(
              (entry) => !entry.suggestedActions
            )
          }
        }
      : riskAssessment
  }
)

export const sortRiskAssessments = createSelector(
  [getRiskAssessmentList, getRiskAssessmentSortType],
  (riskAssessments: Array<RiskAssessment>, sortType) => {
    if (!riskAssessments) {
      return null
    }
    switch (sortType) {
      case 'recent':
        return _.sortBy(riskAssessments, 'createdOn').reverse()
      case 'high-low': {
        const noRiskLevel = _.filter(riskAssessments, hasNoRiskLevel)
        return _(riskAssessments)
          .reject(hasNoRiskLevel)
          .sortBy('riskScore')
          .sortBy(sortByRiskLevel)
          .reverse()
          .concat(noRiskLevel)
          .value()
      }
      case 'low-high':
        return _(riskAssessments)
          .sortBy('riskScore')
          .sortBy(sortByRiskLevel)
          .value()
      case 'a-z':
        return _.sortBy(riskAssessments, 'form.name')
      default:
        return riskAssessments
    }
  }
)

/**
 * Calculates risk assessment score based on user-generated form specification
 * and current form data in state.
 *
 * @param {Object} fields the fields from the user-generated form specification,
 * built by the FormBuilder library. Contains non-LmC properties, such as
 * `values` instead of `options`
 * @param {Object} form the LmC form, which can be either a form object stored in
 * the database (e.g. a completed risk assessment), or an incomplete form state
 * derived from Redux.
 */
const getRiskScore = (
  fields: Array<FormField>,
  form: { fields?: Record<string, unknown>; formData?: Record<string, unknown> }
) => {
  let score = 0
  const formFields = (form.fields || form.formData || {}) as Record<
    string,
    unknown
  >

  for (const field of fields) {
    // Don't take hidden fields into account for score
    if (isFieldDependentAndHidden(field, formFields)) {
      continue
    }
    const formValue = _.get(formFields, field.key) as any

    if (!field.values || !field.values.length) {
      if (formValue || formValue === 0) {
        score += formValue
        continue
      }
    }

    const values = _.map(field.values, 'value')
    if (formValue || formValue === 0) {
      score += Array.isArray(formValue)
        ? formValue.reduce((a, b) => a + values[b], 0)
        : values[formValue]
    }
  }
  return score
}

/**
 * Takes a form field specification generated by users with the FormBuilder
 * library, and returns a version with modified keys. The new keys reflect the
 * keys used elsewhere in the app for forms and form data, such as `options`
 * instead of `values`.
 *
 * @param {Object} field
 */
export const formatFieldWithKeys = (field: any) => {
  let formattedField: any = {
    ...field,
    keys: field.key,
    label: field.label,
    layout: 'no-label',
    required: true,
    type: field.type
  }
  if (field.description) {
    formattedField.note = field.description
  }
  if (field.displayCalendar) {
    formattedField.displayCalendar = true
  }
  if (field.formFieldStyle) {
    formattedField.formFieldStyle = field.formFieldStyle
  }
  if (field.hasComment) {
    formattedField.hasComment = true
  }
  if (field.placeholder) {
    formattedField.placeholders = field.placeholder
  }
  if (field.style) {
    formattedField.style = field.style
  }
  if (field.validationTypes) {
    formattedField.validationTypes = field.validationTypes
  }
  if (field.values) {
    formattedField.options = field.values
  }
  if (field.dependsOnKey) {
    formattedField = {
      ...formattedField,
      dependsOn: {
        key: field.dependsOnKey,
        value: field.dependsOnValue
      }
    }
  }
  if (field.suggestedActions) {
    formattedField.suggestedActions = field.suggestedActions
  }
  return formattedField as unknown
}

const formatLabelsWithResidentInfo = (
  form: Form,
  resident: Resident,
  preferredPeopleNominative: string | undefined
) => ({
  ...form,
  formFields: form.formFields
    ? form.formFields.map((field) => ({
        ...field,
        label: formatWithResidentInfo(
          field.label,
          resident,
          preferredPeopleNominative
        ),
        ...(field.description && {
          description: formatWithResidentInfo(
            field.description,
            resident,
            preferredPeopleNominative
          )
        }),
        ...(field.values && {
          values: field.values.map((value) => ({
            ...value,
            label: formatWithResidentInfo(
              value.label,
              resident,
              preferredPeopleNominative
            ),
            ...(typeof value.description === 'string' &&
              value.description.length > 0 && {
                description: formatWithResidentInfo(
                  value.description,
                  resident,
                  preferredPeopleNominative
                )
              })
          }))
        }),
        ...(typeof field.suggestedActions === 'string' &&
          field.suggestedActions.length > 0 && {
            suggestedActions: formatWithResidentInfo(
              field.suggestedActions,
              resident,
              preferredPeopleNominative
            )
          })
      }))
    : null
})

const hasNoRiskLevel = (assessment: RiskAssessment) => !assessment.riskLevel

const isAllRiskFieldsFilled = (
  riskFields: Array<FormField>,
  formFields: Record<string, unknown>
) => {
  for (const field of riskFields) {
    if (isFieldDependentAndHidden(field, formFields)) {
      continue
    }
    if (!_.has(formFields, field.key)) {
      return false
    }
  }
  return true
}

const isFieldDependentAndHidden = (
  field: FormField,
  allFields: Record<string, unknown>
) =>
  field.dependsOnKey &&
  isDependentFieldHidden(allFields, {
    key: field.dependsOnKey,
    value: field.dependsOnValue
  })

const sortByRiskLevel = (assessment: RiskAssessment) => {
  const riskLevels = {
    na: 1,
    low: 2,
    medium: 3,
    high: 4,
    severe: 5
  }
  return riskLevels[assessment.riskLevel as keyof typeof riskLevels] || 0
}
